import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Trash2, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GRADES = ['Grade R', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];
const SUBJECTS = ['Mathematics', 'English', 'Afrikaans', 'Life Skills', 'Natural Sciences', 'Social Sciences', 'Technology', 'Economic Management Sciences', 'Life Orientation', 'Physical Sciences', 'Accounting', 'Business Studies'];

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    selected_grades: [],
    selected_subjects: [],
    resources_text: '',
    learner_profile_text: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/teacher/settings`);
      setSettings(response.data);
      setFormData({
        selected_grades: response.data.selected_grades || [],
        selected_subjects: response.data.selected_subjects || [],
        resources_text: response.data.resources_text || '',
        learner_profile_text: response.data.learner_profile_text || ''
      });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.selected_grades.length > 3) {
      toast.error('Maximum 3 grades allowed');
      return;
    }
    if (formData.selected_subjects.length > 5) {
      toast.error('Maximum 5 subjects allowed');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/teacher/settings`, formData);
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    }
  };

  const toggleGrade = (grade) => {
    setFormData(prev => ({
      ...prev,
      selected_grades: prev.selected_grades.includes(grade)
        ? prev.selected_grades.filter(g => g !== grade)
        : prev.selected_grades.length < 3
        ? [...prev.selected_grades, grade]
        : prev.selected_grades
    }));
  };

  const toggleSubject = (subject) => {
    setFormData(prev => ({
      ...prev,
      selected_subjects: prev.selected_subjects.includes(subject)
        ? prev.selected_subjects.filter(s => s !== subject)
        : prev.selected_subjects.length < 5
        ? [...prev.selected_subjects, subject]
        : prev.selected_subjects
    }));
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-sky-50">Loading...</div>;

  return (
    <div className="min-h-screen bg-sky-50" data-testid="settings-page">
      <header className="glass-header py-4 px-6">
        <div className="max-w-4xl mx-auto">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="glass-card p-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Settings</h2>
          <p className="text-slate-600 mb-8">Configure your grades, subjects, and teaching context</p>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Select Grades (Maximum 3)</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {GRADES.map(grade => (
                  <button
                    key={grade}
                    type="button"
                    onClick={() => toggleGrade(grade)}
                    className={`px-4 py-3 rounded-xl border-2 font-medium transition-all ${
                      formData.selected_grades.includes(grade)
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white/50 text-slate-700 border-slate-200 hover:border-teal-300'
                    }`}
                    data-testid={`grade-${grade.replace(' ', '-')}`}
                  >
                    {grade}
                  </button>
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Selected: {formData.selected_grades.length}/3
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Select Subjects (Maximum 5)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {SUBJECTS.map(subject => (
                  <button
                    key={subject}
                    type="button"
                    onClick={() => toggleSubject(subject)}
                    className={`px-4 py-3 rounded-xl border-2 font-medium transition-all text-left ${
                      formData.selected_subjects.includes(subject)
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white/50 text-slate-700 border-slate-200 hover:border-teal-300'
                    }`}
                    data-testid={`subject-${subject.replace(' ', '-')}`}
                  >
                    {subject}
                  </button>
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Selected: {formData.selected_subjects.length}/5
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Available Resources</h3>
              <textarea
                value={formData.resources_text}
                onChange={(e) => setFormData({ ...formData, resources_text: e.target.value })}
                className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500 min-h-32"
                placeholder="Describe the resources available in your classroom (e.g., textbooks, technology, manipulatives)..."
                data-testid="resources-text"
              />
            </div>

            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Learner Profile</h3>
              <textarea
                value={formData.learner_profile_text}
                onChange={(e) => setFormData({ ...formData, learner_profile_text: e.target.value })}
                className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500 min-h-32"
                placeholder="Describe your learners: barriers to learning, gifted learners, support needs, language considerations..."
                data-testid="learner-profile-text"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6"
              data-testid="save-settings-button"
            >
              Save Settings
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}